/**
 * Carro lateral (sections/mx-cart-drawer.liquid).
 *
 * - Se abre al agregar un producto (Loft emite `theme:ajaxCart:addSuccess` en <body>) y al tocar
 *   el ícono del carro del encabezado.
 * - Cambiar cantidades usa /cart/change.js y pide de vuelta el HTML de la sección, así el carro
 *   siempre muestra lo que Shopify calcula (precios, descuentos, envío gratis).
 * - Las sugerencias vienen de la sección mx-cart-recommendations.
 */

const ADD_SUCCESS_EVENT = 'theme:ajaxCart:addSuccess';

/** En el editor de temas: la sección se vuelve a dibujar al cambiar un ajuste y debe seguir abierta. */
let selectedInEditor = false;

/** Bloquea el scroll de la página sin que el contenido salte al desaparecer la barra de desplazamiento. */
function lockPageScroll() {
  const root = document.documentElement;
  if (root.dataset.mxCartLocked) return;
  const scrollbar = window.innerWidth - root.clientWidth;
  root.dataset.mxCartLocked = 'true';
  root.style.overflow = 'hidden';
  if (scrollbar > 0) root.style.paddingRight = `${scrollbar}px`;
}

function unlockPageScroll() {
  const root = document.documentElement;
  delete root.dataset.mxCartLocked;
  root.style.overflow = '';
  root.style.paddingRight = '';
}

class MxCartDrawer extends HTMLElement {
  /** @type {HTMLDialogElement | null} */
  dialog = null;
  /** @type {AbortController | null} */
  #pending = null;
  /** @type {Map<string, string>} */
  #recsCache = new Map();

  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    if (!this.dialog) return;

    this.addEventListener('click', this.#onClick);
    this.addEventListener('change', this.#onChange);
    this.dialog.addEventListener('cancel', this.#onCancel);
    document.body.addEventListener(ADD_SUCCESS_EVENT, this.#onAdded);
    document.addEventListener('click', this.#onCartIconClick);
    window.addEventListener('pageshow', this.#onPageShow);

    if (this.hasAttribute('data-design-mode')) this.#setupEditor();
  }

  disconnectedCallback() {
    document.body.removeEventListener(ADD_SUCCESS_EVENT, this.#onAdded);
    document.removeEventListener('click', this.#onCartIconClick);
    window.removeEventListener('pageshow', this.#onPageShow);
    this.#editorCleanup?.();
    if (this.dialog?.open) this.#finishClose();
  }

  get sectionId() {
    return this.dataset.sectionId || '';
  }

  async open() {
    const dialog = this.dialog;
    if (!dialog) return;

    dialog.classList.remove('is-closing');
    if (!dialog.open) {
      lockPageScroll();
      try {
        dialog.showModal();
        // El foco va al botón cerrar sin que el navegador desplace el panel mientras entra animado.
        dialog.scrollLeft = 0;
        this.querySelector('[data-mx-cart-close]')?.focus({ preventScroll: true });
      } catch {
        // Si el navegador no puede abrirlo como modal, no dejamos la página bloqueada.
        unlockPageScroll();
        window.location.href = this.dataset.cartUrl || '/cart';
        return;
      }
    }

    // Si por cualquier motivo el contenido quedó vacío, lo volvemos a pedir.
    if (!this.querySelector('[data-mx-cart-content] .mx-cart__body')?.children.length) this.refresh();

    this.#loadRecommendations();
  }

  close() {
    const dialog = this.dialog;
    if (!dialog?.open || dialog.classList.contains('is-closing')) return;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.#finishClose();
      return;
    }

    dialog.classList.add('is-closing');
    const panel = dialog.querySelector('.mx-cart__panel');
    const done = () => {
      window.clearTimeout(fallback);
      panel?.removeEventListener('animationend', done);
      this.#finishClose();
    };
    const fallback = window.setTimeout(done, 400);
    panel?.addEventListener('animationend', done);
  }

  #finishClose() {
    if (!this.dialog) return;
    this.dialog.classList.remove('is-closing');
    if (this.dialog.open) this.dialog.close();
    unlockPageScroll();
  }

  /** Vuelve a pedir el carro a Shopify y reemplaza el contenido. */
  async refresh() {
    const html = await this.#fetchSection(`${this.dataset.cartUrl}?section_id=${encodeURIComponent(this.sectionId)}`);
    if (html) this.#render(html);
    else if (this.dialog?.open) this.#renderFallback();
  }

  /** Si Shopify no responde, en vez de un panel vacío ofrecemos ir a la página del carro. */
  #renderFallback() {
    const body = this.querySelector('[data-mx-cart-content] .mx-cart__body');
    if (!body || body.querySelector('.mx-cart__item, .mx-cart__empty')) return;
    body.innerHTML = `<div class="mx-cart__empty"><p>No pudimos cargar tu carro.</p><a class="mx-cart__button mx-cart__button--primary" href="${
      this.dataset.cartUrl || '/cart'
    }">Ver carro</a></div>`;
  }

  /**
   * @param {number} line - Línea del carro (empieza en 1).
   * @param {number} quantity
   */
  async changeLine(line, quantity) {
    const content = this.querySelector('[data-mx-cart-content]');
    content?.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(`${this.dataset.cartUrl}/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line, quantity, sections: this.sectionId }),
      });
      const data = await response.json();

      if (!response.ok) {
        content?.removeAttribute('aria-busy');
        this.#showError(data.description || data.message || 'No se pudo actualizar el carro.');
        return;
      }

      const html = data.sections?.[this.sectionId];
      if (html) this.#render(html);
      this.#updateHeaderCount(data.item_count);
      this.#announce(quantity === 0 ? 'Producto eliminado del carro' : 'Carro actualizado');
    } catch {
      content?.removeAttribute('aria-busy');
      this.#showError('No se pudo actualizar el carro. Revisa tu conexión e inténtalo de nuevo.');
    }
  }

  /** @param {string} variantId */
  async addVariant(variantId) {
    const content = this.querySelector('[data-mx-cart-content]');
    content?.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(`${this.dataset.cartUrl}/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }], sections: this.sectionId }),
      });
      const data = await response.json();

      if (!response.ok) {
        content?.removeAttribute('aria-busy');
        this.#showError(data.description || 'No se pudo agregar el producto.');
        return;
      }

      const html = data.sections?.[this.sectionId];
      if (html) this.#render(html);
      else await this.refresh();
      this.#syncHeaderCount();
      this.#announce('Producto agregado al carro');
    } catch {
      content?.removeAttribute('aria-busy');
      this.#showError('No se pudo agregar el producto. Revisa tu conexión e inténtalo de nuevo.');
    }
  }

  /** @param {string} url */
  async #fetchSection(url) {
    this.#pending?.abort();
    this.#pending = new AbortController();

    try {
      const response = await fetch(url, { signal: this.#pending.signal });
      if (!response.ok) return null;
      return await response.text();
    } catch {
      return null;
    }
  }

  /** @param {string} sectionHtml - HTML completo de la sección devuelto por Shopify. */
  #render(sectionHtml) {
    const doc = new DOMParser().parseFromString(sectionHtml, 'text/html');
    const fresh = doc.querySelector('[data-mx-cart-content]');
    const current = this.querySelector('[data-mx-cart-content]');
    if (!fresh || !current) {
      if (this.dialog?.open) this.#renderFallback();
      return;
    }

    current.replaceWith(fresh);
    this.#loadRecommendations();
    // Avisa a otras partes de la página (p. ej. la barra de envío gratis) que el carro cambió.
    document.dispatchEvent(new CustomEvent('mx:cart:changed'));
  }

  async #loadRecommendations() {
    if (this.dataset.recommendations !== 'true') return;

    const container = this.querySelector('[data-mx-cart-recs]');
    const productId = container?.getAttribute('data-product-id');
    if (!container || !productId) return;

    let html = this.#recsCache.get(productId);
    if (!html) {
      const url = `${this.dataset.recommendationsUrl}?section_id=mx-cart-recommendations&product_id=${encodeURIComponent(
        productId
      )}&limit=10&intent=related`;
      try {
        const response = await fetch(url);
        if (!response.ok) return;
        html = await response.text();
        this.#recsCache.set(productId, html);
      } catch {
        return;
      }
    }

    // El carro pudo volver a dibujarse mientras se cargaban las sugerencias.
    const target = this.querySelector('[data-mx-cart-recs]');
    if (!target || target.getAttribute('data-product-id') !== productId) return;

    const list = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-mx-recs-list]');
    if (!list) {
      target.replaceChildren();
      return;
    }

    const title = list.querySelector('.mx-cart__recs-title');
    const heading = target.getAttribute('data-heading');
    if (title && heading) title.textContent = heading;
    target.replaceChildren(list);

    // Quita sugerencias que ya están en el carro (la caché puede ser de antes de agregarlas).
    const inCart = new Set(
      Array.from(this.querySelectorAll('.mx-cart__item-title'), (link) => link.getAttribute('href')?.split('?')[0])
    );
    for (const card of target.querySelectorAll('.mx-cart__rec')) {
      const href = card.querySelector('a')?.getAttribute('href')?.split('?')[0];
      if (href && inCart.has(href)) card.remove();
    }
    if (!target.querySelector('.mx-cart__rec')) target.replaceChildren();
  }

  /** @param {number} count */
  #updateHeaderCount(count) {
    for (const id of ['cart-svg-count', 'mobile-cart-svg-count']) {
      const badge = document.getElementById(id);
      if (!badge) continue;
      badge.textContent = String(count);
      badge.classList.toggle('cart-show', count > 0);
      badge.classList.toggle('cart-extra-pad', count > 9);
      badge.style.display = count > 0 ? 'block' : 'none';
    }
    for (const el of document.querySelectorAll('#CartCount')) el.textContent = String(count);
    document.querySelector('.js-cart-icon')?.classList.toggle('cart-show', count > 0);
  }

  async #syncHeaderCount() {
    try {
      const response = await fetch(`${this.dataset.cartUrl}.js`);
      const cart = await response.json();
      this.#updateHeaderCount(cart.item_count);
    } catch {}
  }

  /** @param {string} message */
  #showError(message) {
    const error = this.querySelector('[data-mx-cart-error]');
    if (!error) return;
    error.textContent = message;
    error.hidden = false;
  }

  /** @param {string} message */
  #announce(message) {
    const status = this.querySelector('[data-mx-cart-status]');
    if (status) status.textContent = message;
  }

  /** Al volver con el botón Atrás, el navegador puede restaurar la página con el carro abierto. */
  /** @param {PageTransitionEvent} event */
  #onPageShow = (event) => {
    if (!event.persisted) return;
    this.#finishClose();
    this.refresh();
  };

  #onAdded = async () => {
    await this.refresh();
    this.#syncHeaderCount();
    if (this.dataset.openOnAdd === 'true') this.open();
  };

  /** @param {MouseEvent} event */
  #onCartIconClick = (event) => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey) return;

    const target = /** @type {Element} */ (event.target);
    const link = target.closest?.('a.js-cart-icon, a.js-cart-mobile-icon, a[href$="/cart"].header__individual-icon');
    if (!link || this.contains(link)) return;

    // En la página del carro el ícono sigue funcionando como siempre.
    if (window.location.pathname === this.dataset.cartUrl) return;

    event.preventDefault();
    this.refresh();
    this.open();
  };

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = /** @type {Element} */ (event.target);

    if (target.closest('[data-mx-cart-close]') || target === this.dialog) {
      this.close();
      return;
    }

    const qty = target.closest('[data-mx-qty]');
    if (qty) {
      this.changeLine(Number(qty.getAttribute('data-line')), Math.max(0, Number(qty.getAttribute('data-quantity'))));
      return;
    }

    const add = target.closest('[data-mx-add]');
    if (add) {
      this.addVariant(add.getAttribute('data-mx-add') || '');
      return;
    }

    // Enlace a otra página (producto, carro completo): cerramos sin animación para no dejar la página bloqueada.
    if (target.closest('a[href]')) this.#finishClose();
  };

  /** Selector de formato en una sugerencia: actualiza precio y variante a agregar. */
  /** @param {Event} event */
  #onChange = (event) => {
    const select = /** @type {HTMLSelectElement} */ (event.target);
    if (!select.matches?.('[data-mx-variant-select]')) return;

    const card = select.closest('.mx-cart__rec');
    const option = select.selectedOptions[0];
    if (!card || !option) return;

    card.querySelector('[data-mx-add]')?.setAttribute('data-mx-add', option.value);
    const price = card.querySelector('[data-mx-rec-price]');
    if (price && option.dataset.price) price.textContent = option.dataset.price;
  };

  /** @param {Event} event */
  #onCancel = (event) => {
    event.preventDefault();
    this.close();
  };

  /** @type {(() => void) | null} */
  #editorCleanup = null;

  #setupEditor() {
    /** @param {Event & { detail?: { sectionId?: string } }} event */
    const onSelect = (event) => {
      if (event.detail?.sectionId !== this.sectionId) return;
      selectedInEditor = true;
      this.open();
    };
    /** @param {Event & { detail?: { sectionId?: string } }} event */
    const onDeselect = (event) => {
      if (event.detail?.sectionId !== this.sectionId) return;
      selectedInEditor = false;
      this.close();
    };

    document.addEventListener('shopify:section:select', onSelect);
    document.addEventListener('shopify:section:deselect', onDeselect);
    this.#editorCleanup = () => {
      document.removeEventListener('shopify:section:select', onSelect);
      document.removeEventListener('shopify:section:deselect', onDeselect);
    };

    if (selectedInEditor) this.open();
  }
}

if (!customElements.get('mx-cart-drawer')) customElements.define('mx-cart-drawer', MxCartDrawer);
